import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface LegalContentRendererProps {
  content: string;
  loading: boolean;
  error: string | null;
}

export const LegalContentRenderer: React.FC<LegalContentRendererProps> = ({
  content,
  loading,
  error
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-white/60">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-400 text-center py-8">
        Error loading content: {error}
      </div>
    );
  }

  return (
    <div className="prose prose-invert prose-lg max-w-[80vw] w-full overflow-hidden">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="text-2xl font-bold text-white mb-6 break-words">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-xl font-semibold text-white mb-4 mt-8 break-words">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-medium text-white mb-3 mt-6 break-words">{children}</h3>
          ),
          p: ({ children }) => (
            <p className="text-white/90 mb-4 leading-relaxed break-words">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside text-white/90 mb-4 space-y-2 break-words">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside text-white/90 mb-4 space-y-2 break-words">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="text-white/90 break-words">{children}</li>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-white break-words">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic text-white/80 break-words">{children}</em>
          ),
          /*
            The legal documents cross-reference each other — the Terms of Use
            cite the Privacy Policy, the Instructor Agreement cites the Terms —
            and CKLSM-750 turned those mentions into links to `/terms-of-use`,
            `/privacy-policies` and `/independent-contractor-agreement`.

            An in-app route is rendered as a router `Link`, not an anchor with
            `target="_blank"`: the sibling document is the same SPA, so a new
            tab would reload the whole bundle, and a reader walking the three
            documents would finish with three tabs open. External links keep
            the new tab and `rel="noopener noreferrer"`.
          */
          a: ({ href, children }) => {
            /*
              A single leading slash, and not a second `/` or `\` right after
              it: `//evil.example.com` is a protocol-relative URL, and
              `/\evil.example.com` is the same thing in disguise — browsers
              normalize a leading backslash to a forward slash for http/https
              pages. `startsWith('/')` alone would classify either as in-app
              and hand it to the router without `rel="noopener noreferrer"`.
            */
            const isInternal =
              typeof href === 'string' && href.startsWith('/') && !/^\/[\\/]/.test(href);
            const className = 'text-red-400 hover:text-red-300 underline break-words';

            if (isInternal) {
              return (
                <Link to={href} className={className}>
                  {children}
                </Link>
              );
            }

            return (
              <a
                href={href}
                className={className}
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            );
          },
          hr: () => (
            <hr className="border-gray-600 my-8" />
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-red-400 pl-4 italic text-white/80 my-4 break-words">
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto">
              <table className="min-w-full text-white/90 mb-4">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border border-gray-600 px-4 py-2 text-left font-semibold text-white break-words">{children}</th>
          ),
          td: ({ children }) => (
            <td className="border border-gray-600 px-4 py-2 text-white/90 break-words">{children}</td>
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
